import { Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { UserService } from '@services/user/user.service';
import { PersonalisedPromotion } from '@cureskin/api-client/src/server-api/api';

@Injectable({ providedIn: 'root' })
export class PersonalisedPromotionsService {
  private cachedPromotionsPromise: Promise<PersonalisedPromotion[]> | null = null;

  constructor(
    private readonly conn: ConnectionService,
    private readonly userService: UserService,
  ) {}

  /**
   * Fetch personalised promotions once per session-instance and memoise the
   * result. All callers share the same in-flight promise; subsequent calls
   * resolve from the in-memory cache (no extra API hits).
   */
  loadPersonalisedPromotions(): Promise<PersonalisedPromotion[]> {
    if (this.cachedPromotionsPromise) {
      return this.cachedPromotionsPromise;
    }
    this.cachedPromotionsPromise = (async (): Promise<PersonalisedPromotion[]> => {
      try {
        return await this.conn.getPersonalisedPromotions();
      } catch {
        this.cachedPromotionsPromise = null; // allow a fresh attempt later
        return [];
      }
    })();
    return this.cachedPromotionsPromise;
  }

  /** Clear the cached promotions (e.g. on logout / user switch). */
  reset(): void {
    this.cachedPromotionsPromise = null;
  }

  async isAOVActiveInPersonalisedPromotions(user: any): Promise<boolean> {
    if (!user || !this.userService.isPaidUser(user)) {
      return false;
    }
    const promotions = await this.loadPersonalisedPromotions();
    return promotions.includes(PersonalisedPromotion.AOV);
  }
}
